import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ChildContactRelationshipsService } from '@/api/childContactRelationships';
import type {
  CreateChildContactRelationshipRequest,
  UpdateChildContactRelationshipRequest,
  UpdatePermissionsRequest
} from '@/types/childContactRelationship';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { useModuleGate } from './useAccess';
import { PICKUP_CONTACT_READ_MODULES, RELATIONSHIP_READ_MODULES } from '@/lib/modules';

// Query keys for relationships
export const relationshipKeys = {
  all: ['relationships'] as const,
  lists: () => [...relationshipKeys.all, 'list'] as const,
  byChild: (childId: string) => [...relationshipKeys.lists(), 'child', childId] as const,
  byContact: (contactId: string) => [...relationshipKeys.lists(), 'contact', contactId] as const,
  pickup: (childId: string) => [...relationshipKeys.all, 'pickup', childId] as const,
  details: () => [...relationshipKeys.all, 'detail'] as const,
  detail: (id: string) => [...relationshipKeys.details(), id] as const,
  audit: (id: string) => [...relationshipKeys.all, 'audit', id] as const,
  expired: () => [...relationshipKeys.all, 'expired'] as const,
};

// Hook to fetch relationships for a specific child
export function useChildRelationships(childId: string, enabled = true) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  // REL_READ: the server admits these modules only (#1670).
  const canReadRelationships = useModuleGate(RELATIONSHIP_READ_MODULES);

  return useQuery({
    queryKey: relationshipKeys.byChild(childId),
    queryFn: async () => {
      const response = await ChildContactRelationshipsService.listRelationshipsByChild(childId, 100, 0);
      return response.relationships || [];
    },
    enabled: !!childId && isSignedIn && isAuthReady && enabled && canReadRelationships,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to fetch relationships for a specific contact
export function useContactRelationships(contactId: string, enabled = true) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: relationshipKeys.byContact(contactId),
    queryFn: async () => {
      const response = await ChildContactRelationshipsService.listRelationshipsByContact(contactId, 100, 0);
      return response.relationships || [];
    },
    enabled: !!contactId && isSignedIn && isAuthReady && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to fetch pickup-authorized contacts for a child
export function usePickupContacts(childId: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  // REL_READ plus attendance-activities: the server admits these modules only (#1691).
  const canReadPickup = useModuleGate(PICKUP_CONTACT_READ_MODULES);

  return useQuery({
    queryKey: relationshipKeys.pickup(childId),
    queryFn: async () => {
      // The server answers a bare array, not a { relationships } envelope.
      return (await ChildContactRelationshipsService.getPickupContacts(childId)) ?? [];
    },
    enabled: !!childId && isSignedIn && isAuthReady && canReadPickup,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to fetch a specific relationship
export function useRelationship(id: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: relationshipKeys.detail(id),
    queryFn: () => ChildContactRelationshipsService.getRelationship(id),
    enabled: !!id && isSignedIn && isAuthReady,
    staleTime: 0, // Always considered stale to ensure fresh data
  });
}

// Hook to create a new relationship
export function useCreateRelationship() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ childId, data }: { childId: string; data: CreateChildContactRelationshipRequest }) =>
      ChildContactRelationshipsService.createRelationship(childId, data),
    onMutate: async ({ childId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: relationshipKeys.byChild(childId) });
      
      // Snapshot the previous value
      const previousRelationships = queryClient.getQueryData(relationshipKeys.byChild(childId));
      
      // Optimistically update with temporary data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(relationshipKeys.byChild(childId), (old: any[]) => {
        const tempRelationship = {
          id: `temp-${Date.now()}`,
          childId,
          contactId: data.contactId,
          relationshipType: data.relationshipType,
          permissions: data.permissions || [],
          validFrom: data.validFrom,
          validTo: data.validTo,
          // Blank placeholder name until the refetch on success fills it in;
          // a literal "Loading..." would show untranslated.
          contactFirstname: '',
          contactLastname: '',
          contactEmail: '',
          contactPhone: '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return [...(old || []), tempRelationship];
      });
      
      // Return context with snapshot
      return { previousRelationships, childId };
    },
    onSuccess: (_response, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: relationshipKeys.byChild(variables.childId) });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.byContact(variables.data.contactId) });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.pickup(variables.childId) });
      
      toast.success(i18n._(msg`Relationship created successfully`));
    },
    onError: (error: Error, _variables, context) => {
      // Roll back optimistic update on error
      if (context?.previousRelationships) {
        queryClient.setQueryData(
          relationshipKeys.byChild(context.childId), 
          context.previousRelationships
        );
      }
      
      const errorMessage = error.message || "Unknown error";
      const lowerMessage = errorMessage.toLowerCase();
      
      // Check for duplicate relationship
      if (lowerMessage.includes("already exists") || lowerMessage.includes("duplicate")) {
        toast.error(i18n._(msg`This relationship already exists`));
        return;
      }
      
      // Check for validation errors
      if (lowerMessage.includes("validation") || lowerMessage.includes("invalid")) {
        toast.error(i18n._(msg`Please check the form data and try again`));
        return;
      }
      
      // Generic error fallback
      toast.error(i18n._(msg`Failed to create relationship`));
    },
  });
}

// Hook to update a relationship
export function useUpdateRelationship() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateChildContactRelationshipRequest }) =>
      ChildContactRelationshipsService.updateRelationship(id, data),
    onSuccess: (response) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: relationshipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.detail(response.id) });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.audit(response.id) });
      
      // Update the detail query data directly
      queryClient.setQueryData(relationshipKeys.detail(response.id), response);
      
      toast.success(i18n._(msg`Relationship updated successfully`));
    },
    onError: (error: Error) => {
      const errorMessage = error.message || "Unknown error";
      const lowerMessage = errorMessage.toLowerCase();
      
      // Check for validation errors
      if (lowerMessage.includes("validation") || lowerMessage.includes("invalid")) {
        toast.error(i18n._(msg`Please check the form data and try again`));
        return;
      }
      
      // Check for concurrent modification
      if (lowerMessage.includes("concurrent") || lowerMessage.includes("modified")) {
        toast.error(i18n._(msg`The relationship has been modified by another user. Please refresh and try again.`));
        return;
      }
      
      // Generic error fallback
      toast.error(i18n._(msg`Failed to update relationship`));
    },
  });
}

type CustodyListSnapshot = Array<[readonly unknown[], unknown]>;

// Hook to record custody (Sorgerecht) on a relationship (#2097). Sends only
// hasCustody, so the server keeps every other field; the cached lists flip at
// once and roll back on error.
export function useUpdateCustody() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, hasCustody }: { id: string; hasCustody: boolean }) =>
      ChildContactRelationshipsService.updateRelationship(id, { hasCustody }),
    onMutate: async ({ id, hasCustody }) => {
      await queryClient.cancelQueries({ queryKey: relationshipKeys.lists() });
      const snapshot: CustodyListSnapshot = queryClient.getQueriesData({ queryKey: relationshipKeys.lists() });
      snapshot.forEach(([queryKey, data]) => {
        if (Array.isArray(data)) {
          queryClient.setQueryData(
            queryKey,
            data.map((r: { id: string }) => (r.id === id ? { ...r, hasCustody } : r)),
          );
        }
      });
      return { snapshot };
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: relationshipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.detail(response.id) });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.audit(response.id) });

      toast.success(i18n._(msg`Custody updated successfully`));
    },
    onError: (_error: Error, _variables, context) => {
      context?.snapshot.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
      toast.error(i18n._(msg`Failed to update custody`));
    },
  });
}

// Hook to update relationship permissions only
export function useUpdatePermissions() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: UpdatePermissionsRequest }) =>
      ChildContactRelationshipsService.updatePermissions(id, permissions),
    onSuccess: (response) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: relationshipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.detail(response.id) });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.pickup(response.childId) });
      
      toast.success(i18n._(msg`Permissions updated successfully`));
    },
    onError: (_error: Error) => {
      toast.error(i18n._(msg`Failed to update permissions`));
    },
  });
}

// Hook to delete a relationship
export function useDeleteRelationship() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      ChildContactRelationshipsService.deleteRelationship(id),
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: relationshipKeys.all });
      
      // Store snapshots for all potentially affected queries
      const snapshots: Record<string, unknown> = {};
      
      // Get all queries that might contain this relationship
      const queries = queryClient.getQueriesData({ queryKey: relationshipKeys.lists() });
      
      queries.forEach(([queryKey, queryData]) => {
        if (queryData) {
          snapshots[JSON.stringify(queryKey)] = queryData;
          
          // Optimistically remove the relationship from the list
          if (Array.isArray(queryData)) {
            queryClient.setQueryData(
              queryKey,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              queryData.filter((r: any) => r.id !== id)
            );
          }
        }
      });
      
      // Return context with snapshots
      return { snapshots };
    },
    onSuccess: (_, _variables) => {
      // Invalidate all relationship queries
      queryClient.invalidateQueries({ queryKey: relationshipKeys.all });
      
      toast.success(i18n._(msg`Relationship deleted successfully`));
    },
    onError: (_error: Error, _variables, context) => {
      // Roll back all optimistic updates on error
      if (context?.snapshots) {
        Object.entries(context.snapshots).forEach(([key, data]) => {
          const queryKey = JSON.parse(key);
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      toast.error(i18n._(msg`Failed to delete relationship`));
    },
  });
}

// Hook to get expired relationships
export function useExpiredRelationships() {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: relationshipKeys.expired(),
    queryFn: async () => {
      const response = await ChildContactRelationshipsService.getExpiredRelationships(50, 0);
      return response.relationships || [];
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to get relationship audit history
export function useRelationshipAuditHistory(id: string, limit = 20, offset = 0) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: [...relationshipKeys.audit(id), { limit, offset }],
    queryFn: () => ChildContactRelationshipsService.getAuditHistory(id, limit, offset),
    enabled: !!id && isSignedIn && isAuthReady,
  });
}